import React, { useState, useEffect } from 'react';
import { TextField, Button, MenuItem, Box, Typography, Grid } from '@mui/material';
import PhotoEditDialog from './pe'; // Импортируем окно редактирования фото
import './adf.css'; // Внешний вид

const categories = [
  { value: 'Инструменты', label: 'Инструменты' },
  { value: 'Одежда', label: 'Одежда' },
  { value: 'Спорт', label: 'Спортивный инвентарь' },
  { value: 'Техника', label: 'Техника' },
  { value: 'Транспорт', label: 'Транспорт' },
];

const subcategories = [
  'Инструменты',
  'Одежда',
  'Спортивный инвентарь',
  'Техника',
  'Транспорт',
];

const categorySpecificFields = {
  Инструменты: ['Бренд', 'Мощность', 'Комплектация'],
  Одежда: ['Размер общий', 'Размер российский', 'Материал', 'Пол', 'Сезон', 'Состояние'],
  Спорт: ['Тип', 'Бренд', 'Размер', 'Возраст', 'Комплектация'],
  Техника: ['Бренд', 'Модель', 'Состояние', 'Комплектация'],
  Транспорт: ['Тип', 'Марка и модель', 'Год выпуска', 'Тип кузова', 'Категория В/У'],
};

const AddItemForm = ({ initialData = {}, onItemAdded, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    subcategories: [],
    specificFields: {},
    photos: [],
    ...initialData, // Заполняем начальными данными, если переданы
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, ...initialData })); // Обновляем форму при изменении `initialData`
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleSubcategory = (subcategory) => {
    setFormData((prev) => {
      const subcategories = prev.subcategories.includes(subcategory)
        ? prev.subcategories.filter((item) => item !== subcategory)
        : [...prev.subcategories, subcategory];
      return { ...prev, subcategories };
    });
  };

  const handleSpecificFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      specificFields: { ...prev.specificFields, [field]: value },
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = [...photoFiles, ...files].slice(0, 10);
    setPhotoFiles(newFiles);
    setFormData((prev) => ({
      ...prev,
      photos: newFiles.map((file) => URL.createObjectURL(file)),
    }));
  };

  const openEditDialog = () => setEditDialogOpen(true);
  const closeEditDialog = () => setEditDialogOpen(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const isEditing = !!initialData.id; // Определяем режим: добавление или редактирование

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('subcategories', JSON.stringify(formData.subcategories));
      formDataToSend.append('specificFields', JSON.stringify(formData.specificFields));
      photoFiles.forEach((file) => {
        formDataToSend.append('photos', file);
      });

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/items${isEditing ? `/${initialData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        throw new Error(isEditing ? 'Ошибка при редактировании товара' : 'Ошибка при добавлении товара');
      }

      const result = await response.json();
      alert(isEditing ? 'Товар успешно отредактирован!' : 'Товар успешно добавлен!');
      if (onItemAdded) onItemAdded(result);
    } catch (error) {
      console.error('Ошибка:', error.message);
      alert('Произошла ошибка.');
    }
  };

  return (
    <Box className="add-item-form" sx={{
      width: '80%',
      maxWidth: 500,
      maxHeight: '90vh',
      overflowY: 'auto',
      backgroundColor: 'background.paper',
      p: 4,
      borderRadius: 2,
      boxShadow: 24,
      margin: '0 auto', // Центрирование
      position: 'relative', // Убираем абсолютное позиционирование
      top: 'unset',
      left: 'unset',
      transform: 'unset',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px', position: 'relative' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
          {initialData.id ? 'Редактирование товара' : 'Добавление товара'}
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: '4px',
            top: '50%',
            transform: 'translateY(-100%)',
            minWidth: '32px',
            height: '32px',
            padding: 0,
            color: '#d32f2f',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              color: '#b71c1c',
            },
          }}
        >
          &times;
        </Button>
      </Box>
      <Box className="add-item-form__scrollable">
        <TextField
          label="Категория"
          name="category"
          value={formData.category}
          onChange={handleInputChange}
          select
          fullWidth
          required
        >
          {categories.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant="subtitle1">Дополнительные категории:</Typography>
        <Grid container spacing={1}>
          {subcategories.map((subcategory) => (
            <Grid item xs={6} sm={4} key={subcategory}>
              <label
                className={`add-item-form__subcategory ${
                  formData.subcategories.includes(subcategory) ? 'add-item-form__subcategory--active' : ''
                }`}
              >
                {subcategory}
                <input
                  type="checkbox"
                  hidden
                  checked={formData.subcategories.includes(subcategory)}
                  onChange={() => toggleSubcategory(subcategory)}
                />
              </label>
            </Grid>
          ))}
        </Grid>

        <TextField
          label="Название"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          fullWidth
          required
          sx={{ marginTop: 2 }}
        />

        {[
          ...new Set([
            ...(categorySpecificFields[formData.category] || []),
            ...formData.subcategories.flatMap((subcategory) => categorySpecificFields[subcategory] || []),
          ]),
        ].map((field) => (
          <TextField
            key={field}
            label={field}
            name={field}
            value={formData.specificFields[field] || ''}
            onChange={(e) => handleSpecificFieldChange(field, e.target.value)}
            fullWidth
            required={field === 'Тип'}
            sx={{ marginTop: 2 }}
          />
        ))}

        <TextField
          label="Описание"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          multiline
          rows={3}
          fullWidth
          required
          sx={{ marginTop: 2 }}
        />
        <TextField
          label="Цена (₽/сутки)"
          name="price"
          type="number"
          value={formData.price}
          onChange={handleInputChange}
          fullWidth
          required
          sx={{ marginTop: 2 }}
        />

        {formData.photos.length > 0 && (
          <Box className="photo-preview-container">
            {formData.photos.slice(0, 3).map((photo, index) => (
              <img key={index} src={photo} alt="preview" className="photo-preview" />
            ))}
            {formData.photos.length > 3 && <Typography className="photo-counter">+{formData.photos.length - 3}</Typography>}
          </Box>
        )}

        <Button variant="contained" onClick={openEditDialog} className="add-item-form__upload">
          {photoFiles.length > 0 ? 'Редактировать изображения' : 'Загрузить изображения'}
        </Button>
      </Box>

      <Box className="add-item-form__submit" >
        <Button variant="contained" color="primary" fullWidth onClick={handleFormSubmit}>
          {initialData.id ? 'Сохранить' : 'Добавить товар'}
        </Button>
      </Box>

      <PhotoEditDialog
        open={isEditDialogOpen}
        onClose={closeEditDialog}
        photoFiles={photoFiles}
        setPhotoFiles={setPhotoFiles}
        setFormData={setFormData}
      />
    </Box>
  );
};

export default AddItemForm;
